import React, { useEffect } from 'react';
import styled from 'styled-components';
import { ArrowRight, CalendarDays } from 'lucide-react';
import SEO, { createBreadcrumbStructuredData, createItemListStructuredData } from '../components/SEO';
import { latestUpdates, latestUpdatesLastModified, type LatestUpdate } from '../generated/latestUpdates';
import { useAnalytics } from '../hooks/useAnalytics';
import { borderRadius, breakpoints, colors, spacing, typography } from '../styles/theme';

const SITE_URL = 'https://www.eventfinder.site';
const DESCRIPTION = 'See the latest Event Finder improvements, including better event discovery, worldwide city coverage, faster search, and more accurate map details.';

interface UpdateGroup {
  key: string;
  label: string;
  updates: LatestUpdate[];
}

function formatMonth(date: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T12:00:00.000Z`));
}

function formatDate(date: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T12:00:00.000Z`));
}

function groupUpdates(updates: LatestUpdate[]): UpdateGroup[] {
  const groups = new Map<string, UpdateGroup>();
  for (const update of updates) {
    const key = update.date.slice(0, 7);
    const existing = groups.get(key);
    if (existing) {
      existing.updates.push(update);
    } else {
      groups.set(key, { key, label: formatMonth(update.date), updates: [update] });
    }
  }
  return Array.from(groups.values());
}

function linkLabel(link: string): string {
  if (link === '/groups/new') return 'Create an Event Group';
  if (link === '/my-events') return 'Open My Events';
  if (link === '/map') return 'Explore Events Near Me';
  if (link === '/how-it-works') return 'See how Event Finder works';
  if (link === '/') return 'Visit the home page';
  return 'Browse events';
}

const updateGroups = groupUpdates(latestUpdates);

const Page = styled.article`
  width: 100%;
  min-height: 70vh;
  background: ${colors.background.primary};
`;

const Container = styled.div`
  width: min(100% - ${spacing[8]}, 1040px);
  margin: 0 auto;

  @media (max-width: ${breakpoints.sm}) {
    width: min(100% - ${spacing[6]}, 1040px);
  }
`;

const Hero = styled.header`
  padding: ${spacing[16]} 0 ${spacing[12]};
  border-bottom: 1px solid ${colors.border.normal};
  background: #f4f8fb;

  @media (max-width: ${breakpoints.md}) {
    padding: ${spacing[12]} 0 ${spacing[10]};
  }
`;

const Eyebrow = styled.p`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  margin: 0 0 ${spacing[3]};
  color: ${colors.secondary[700]};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.semibold};
  letter-spacing: 0;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 0 0 ${spacing[4]};
  color: ${colors.text.primary};
  font-size: ${typography.fontSizes['4xl']};
  line-height: ${typography.lineHeights.tight};
  letter-spacing: 0;

  @media (max-width: ${breakpoints.sm}) {
    font-size: ${typography.fontSizes['3xl']};
  }
`;

const Intro = styled.p`
  max-width: 720px;
  margin: 0;
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.lg};
  line-height: ${typography.lineHeights.relaxed};
`;

const Timeline = styled.div`
  padding: ${spacing[12]} 0 ${spacing[16]};
`;

const Month = styled.section`
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  gap: ${spacing[10]};
  margin-bottom: ${spacing[12]};

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${spacing[5]};
  }
`;

const MonthTitle = styled.h2`
  margin: 0;
  color: ${colors.text.primary};
  font-size: ${typography.fontSizes.xl};
  line-height: ${typography.lineHeights.tight};
  letter-spacing: 0;
`;

const UpdateList = styled.div`
  border-top: 1px solid ${colors.border.normal};
`;

const UpdateArticle = styled.article`
  display: grid;
  grid-template-columns: 124px minmax(0, 1fr);
  gap: ${spacing[6]};
  padding: ${spacing[6]} 0 ${spacing[8]};
  border-bottom: 1px solid ${colors.border.normal};

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${spacing[3]};
  }
`;

const UpdateMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

const UpdateDate = styled.time`
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
  line-height: ${typography.lineHeights.normal};
`;

const Category = styled.span`
  color: ${colors.primary[700]};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.bold};
  letter-spacing: 0;
  text-transform: uppercase;
`;

const UpdateTitle = styled.h3`
  margin: 0 0 ${spacing[2]};
  color: ${colors.text.primary};
  font-size: ${typography.fontSizes.xl};
  line-height: ${typography.lineHeights.tight};
  letter-spacing: 0;
`;

const Summary = styled.p`
  margin: 0;
  color: ${colors.text.secondary};
  line-height: ${typography.lineHeights.relaxed};
`;

const Highlights = styled.ul`
  display: grid;
  gap: ${spacing[2]};
  margin: ${spacing[4]} 0 0;
  padding-left: ${spacing[5]};
  color: ${colors.text.secondary};
  line-height: ${typography.lineHeights.relaxed};

  li::marker {
    color: ${colors.secondary[600]};
  }
`;

const UpdateLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  margin-top: ${spacing[4]};
  color: ${colors.primary[700]};
  font-weight: ${typography.fontWeights.semibold};
  text-decoration: none;

  &:hover {
    color: ${colors.primary[500]};
    text-decoration: underline;
  }
`;

const EmptyState = styled.p`
  margin: 0;
  padding: ${spacing[8]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.lg};
  color: ${colors.text.secondary};
  text-align: center;
`;

const LatestUpdatesPage: React.FC = () => {
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    trackEvent({
      action: 'page_view',
      category: 'navigation',
      label: 'latest_updates',
      custom_parameters: {
        page_title: 'Latest Updates',
        page_path: '/latest-updates',
      },
    });
  }, [trackEvent]);

  return (
    <Page data-editorial-page="latest-updates" data-prerender-ready="true">
      <SEO
        title="Latest Event Finder Updates"
        description={DESCRIPTION}
        keywords="Event Finder updates, event search improvements, event discovery updates, event map updates"
        canonical={`${SITE_URL}/latest-updates`}
        type="article"
        modifiedTime={latestUpdatesLastModified}
        languageAlternates={[
          { hrefLang: 'en', href: `${SITE_URL}/latest-updates` },
          { hrefLang: 'x-default', href: `${SITE_URL}/latest-updates` },
        ]}
        structuredData={[
          createBreadcrumbStructuredData([
            { name: 'Home', url: `${SITE_URL}/` },
            { name: 'Latest Updates', url: `${SITE_URL}/latest-updates` },
          ]),
          createItemListStructuredData(
            latestUpdates.map((update) => ({
              name: update.title,
              url: `${SITE_URL}/latest-updates#${update.id}`,
            })),
            'Latest Event Finder updates'
          ),
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'Latest Event Finder Updates',
            description: DESCRIPTION,
            url: `${SITE_URL}/latest-updates`,
            dateModified: latestUpdatesLastModified,
            inLanguage: 'en',
          },
        ]}
      />

      <Hero>
        <Container>
          <Eyebrow><CalendarDays size={17} aria-hidden="true" /> Product changes</Eyebrow>
          <Title>Latest Event Finder Updates</Title>
          <Intro>Recent improvements to event discovery, search, maps, coverage, and data quality. Each note explains what changed for people finding events.</Intro>
        </Container>
      </Hero>

      <Container>
        <Timeline aria-label="Event Finder update history">
          {updateGroups.length ? updateGroups.map((group) => (
            <Month key={group.key} aria-labelledby={`month-${group.key}`}>
              <MonthTitle id={`month-${group.key}`}>{group.label}</MonthTitle>
              <UpdateList>
                {group.updates.map((update) => (
                  <UpdateArticle id={update.id} key={update.id}>
                    <UpdateMeta>
                      <UpdateDate dateTime={update.date}>{formatDate(update.date)}</UpdateDate>
                      <Category>{update.category}</Category>
                    </UpdateMeta>
                    <div>
                      <UpdateTitle>{update.title}</UpdateTitle>
                      <Summary>{update.summary}</Summary>
                      {update.highlights && (
                        <Highlights aria-label={`${update.title} highlights`}>
                          {update.highlights.map((highlight) => <li key={highlight}>{highlight}</li>)}
                        </Highlights>
                      )}
                      {update.link && (
                        <UpdateLink href={update.link}>
                          {linkLabel(update.link)} <ArrowRight size={17} aria-hidden="true" />
                        </UpdateLink>
                      )}
                    </div>
                  </UpdateArticle>
                ))}
              </UpdateList>
            </Month>
          )) : (
            <EmptyState>No public updates are available yet.</EmptyState>
          )}
        </Timeline>
      </Container>
    </Page>
  );
};

export default LatestUpdatesPage;